/**
 * PlatformMoneyFlow: the Lusuku super-admin revenue and settlement view.
 * ─────────────────────────────────────────────────────────────────────────────
 * Answers one question at a glance: of everything collected on completed
 * orders, how much is Lusuku's net revenue, and where did the rest go?
 *
 * BASIS (13 Oct 2026): every figure below is computed on COMPLETED orders,
 * delivered and not cancelled, by the server (orderEconomics.js, the
 * settlement basis). The window's other orders are shown as the pipeline,
 * as counts and gross value, so an open or cancelled order is never read as
 * revenue. Wording is the finance vocabulary: gross collections, payouts,
 * output VAT, payment processing, net revenue.
 *
 * It leads with the LADDER (a waterfall): customer paid, less vendor payouts,
 * rider earnings, 3PL partner shares, VAT on our own commission and the payment
 * rail, ending in what we keep. That last bar is the business. Every figure comes
 * from /api/money/platform, which computes it with the same shared economics
 * service the vendor and partner views use, so the three can never disagree.
 *
 * The rail line is the reason this view exists: the payment rail takes roughly a
 * third of the gross take on a card or mobile-money order, and it used to appear
 * in no report at all.
 *
 * Staff only, country-scoped server-side.
 */

import React, { useMemo } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { apiFetchPlatformMoneyAnalytics } from "../../api/index";
import {
  C,
  S,
  Kpi,
  KpiRow,
  Loading,
  ErrorNote,
  MoneyRow,
  RAIL_LABEL,
  SectionTitle,
  ShareList,
  VEHICLE_LABEL,
  WindowPicker,
  ZONE_LABEL,
  dayLabel,
  fmtPct,
  fmtUGX,
  moneyBarOptions,
  railConfidenceNote,
  useMoneyReport,
  waterfallSeries,
} from "./moneyUi";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const LADDER_COLOR = (kind) => (kind === "in" ? C.teal : kind === "net" ? C.green : C.gold);

export default function PlatformMoneyFlow() {
  const { data, loading, error, days, setDays, refreshedAt } = useMoneyReport(
    (d) => apiFetchPlatformMoneyAnalytics({ days: d }),
    [],
    { days: 30 }
  );

  const totals = data?.totals;
  const pipeline = data?.pipeline;

  const ladderChart = useMemo(() => {
    const ladder = data?.ladder || [];
    if (!ladder.length) return null;
    const { bases, spans } = waterfallSeries(ladder);
    const colors = ladder.map((step) => LADDER_COLOR(step.kind));
    return {
      data: {
        labels: ladder.map((l) => l.label),
        datasets: [
          {
            label: "base",
            data: bases,
            backgroundColor: "rgba(0,0,0,0)",
            stack: "ladder",
            maxBarThickness: 48,
          },
          {
            label: "amount",
            data: spans,
            backgroundColor: colors,
            stack: "ladder",
            borderRadius: 5,
            maxBarThickness: 48,
          },
        ],
      },
      options: {
        ...moneyBarOptions({ stacked: true }),
        plugins: {
          legend: { display: false },
          tooltip: {
            filter: (ctx) => ctx.datasetIndex === 1,
            callbacks: {
              label: (ctx) => {
                const step = ladder[ctx.dataIndex];
                return step.kind === "out"
                  ? `less ${fmtUGX(Math.abs(step.amount))}`
                  : fmtUGX(step.amount);
              },
            },
          },
        },
      },
    };
  }, [data]);

  const trendChart = useMemo(() => {
    const t = data?.trend || [];
    const base = moneyBarOptions({ stacked: true });
    return {
      data: {
        labels: t.map((d) => dayLabel(d.day)),
        datasets: [
          {
            label: "Commission on goods",
            data: t.map((d) => d.commission),
            backgroundColor: C.teal,
            stack: "take",
            maxBarThickness: 26,
          },
          {
            label: "Delivery margin",
            data: t.map((d) => d.deliveryMargin),
            backgroundColor: C.navy,
            stack: "take",
            maxBarThickness: 26,
          },
          {
            label: "Payment processing",
            data: t.map((d) => -d.railFee),
            backgroundColor: C.pink,
            stack: "take",
            maxBarThickness: 26,
          },
          {
            label: "Delivery offers funded by Lusuku",
            data: t.map((d) => -(d.deliveryOfferCost || 0)),
            backgroundColor: C.gold,
            stack: "take",
            maxBarThickness: 26,
          },
        ],
      },
      options: {
        ...base,
        plugins: {
          ...base.plugins,
          tooltip: {
            callbacks: {
              afterBody: (items) => {
                const row = t[items[0]?.dataIndex];
                if (!row) return "";
                return `Net revenue ${fmtUGX(row.netTake)} on ${row.orders} completed order${
                  row.orders === 1 ? "" : "s"
                }`;
              },
              label: (ctx) => `${ctx.dataset.label}: ${fmtUGX(Math.abs(ctx.parsed.y))}`,
            },
          },
        },
      },
    };
  }, [data]);

  const railRows = (data?.byRail || []).map((r) => ({
    key: r.rail,
    label: RAIL_LABEL[r.rail] || r.rail,
    amount: r.customerPaid,
    sub: `${r.orders} order${r.orders === 1 ? "" : "s"} · processing charges ${fmtUGX(r.railFee)}`,
    color: r.rail === "COD" ? C.gold : C.teal,
  }));

  const vehicleRows = (data?.byVehicle || []).map((v) => ({
    key: v.loadClass,
    label: VEHICLE_LABEL[v.loadClass] || v.loadClass,
    amount: v.deliveryFee,
    sub: `${v.orders} trip${v.orders === 1 ? "" : "s"} · margin ${fmtUGX(v.deliveryMargin)}`,
    color: v.loadClass === "MOTORCYCLE" ? C.teal : C.navy,
  }));

  const zoneRows = (data?.byZone || []).map((z) => ({
    key: z.zone,
    label: ZONE_LABEL[z.zone] || z.zone,
    amount: z.deliveryFee,
    sub: `${z.orders} trip${z.orders === 1 ? "" : "s"} · margin ${fmtUGX(z.deliveryMargin)}`,
    color: C.teal,
  }));

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <div>
          <h4 style={S.title}>
            <i className="fas fa-coins" style={{ color: C.gold }} />
            Revenue and settlement
          </h4>
          <div style={S.subtitle}>
            Completed orders only: gross collections, third-party payouts and Lusuku's net revenue
            {refreshedAt ? ` · updated ${refreshedAt.toLocaleTimeString()}` : ""}
          </div>
        </div>
        <WindowPicker value={days} onChange={setDays} />
      </div>

      {loading && <Loading what="the revenue report" />}
      {!loading && error && <ErrorNote>{error}</ErrorNote>}

      {!loading && !error && totals && (
        <>
          <KpiRow>
            <Kpi
              label="Gross collections"
              value={fmtUGX(totals.customerPaid)}
              meta={`${totals.orders} completed order${totals.orders === 1 ? "" : "s"}${
                pipeline ? ` of ${pipeline.placed} placed` : ""
              }`}
              accent={C.teal}
            />
            <Kpi
              label="Net revenue"
              value={fmtUGX(totals.netTake)}
              meta={`${fmtPct(totals.takeRatePct)} net take rate on gross collections`}
              accent={C.green}
            />
            <Kpi
              label="Payment processing cost"
              value={fmtUGX(totals.railNetCost)}
              meta={
                totals.railBorneByCustomer > 0
                  ? `${fmtPct(totals.railShareOfTakePct, 0)} of gross revenue · ${fmtUGX(
                      totals.railBorneByCustomer
                    )} recovered from customers`
                  : `${fmtPct(totals.railShareOfTakePct, 0)} of gross revenue · absorbed by Lusuku`
              }
              accent={C.pink}
            />
            <Kpi
              label="Net revenue per order"
              value={fmtUGX(totals.averageNetTake)}
              meta={`Average order value ${fmtUGX(totals.averageOrderValue)}`}
              accent={C.navy}
            />
          </KpiRow>

          {pipeline && (
            <div style={S.panel}>
              <SectionTitle first>Order pipeline (last {days} days)</SectionTitle>
              <MoneyRow
                label="Orders placed"
                value={`${pipeline.placed}`}
                sub={`Gross order value ${fmtUGX(pipeline.placedValue)}`}
              />
              <MoneyRow
                label="Completed and recognised"
                value={`${pipeline.completed}`}
                sub={`${fmtUGX(pipeline.completedValue)} · ${fmtPct(pipeline.completionRatePct, 0)} completion rate`}
              />
              <MoneyRow
                label="In progress, not yet recognised"
                value={`${pipeline.open}`}
                sub={fmtUGX(pipeline.openValue)}
              />
              <MoneyRow
                label="Cancelled or rejected"
                value={`${pipeline.cancelled}`}
                sub={`${fmtUGX(pipeline.cancelledValue)} · ${fmtPct(pipeline.cancellationRatePct, 0)} cancellation rate`}
              />
            </div>
          )}

          <SectionTitle>Settlement waterfall (last {days} days)</SectionTitle>
          <div style={{ ...S.chartBox, height: 280 }}>
            {ladderChart ? <Bar data={ladderChart.data} options={ladderChart.options} /> : null}
          </div>

          <div style={S.grid2}>
            <div style={S.panel}>
              <SectionTitle first>Revenue</SectionTitle>
              <MoneyRow
                label="Commission on goods"
                value={fmtUGX(totals.commission)}
                sub={
                  totals.goods
                    ? `${fmtPct((totals.commission / totals.goods) * 100)} effective rate on goods value`
                    : null
                }
              />
              <MoneyRow label="Delivery margin (fee less rider and partner payouts)" value={fmtUGX(totals.deliveryMargin)} />
              <MoneyRow label="Gross revenue" value={fmtUGX(totals.grossTake)} />
              <MoneyRow label="Less output VAT on commission" value={fmtUGX(-totals.commissionVat)} />
              {totals.deliveryOfferCost > 0 && (
                <MoneyRow
                  label="Less delivery offers funded by Lusuku"
                  value={fmtUGX(-totals.deliveryOfferCost)}
                  sub={`${totals.ordersWithDeliveryOffer} order${totals.ordersWithDeliveryOffer === 1 ? "" : "s"} with free or reduced delivery (first-order and referral offers)`}
                />
              )}
              <MoneyRow label="Less payment processing charges" value={fmtUGX(-totals.railFee)} />
              {totals.paymentProcessingFee > 0 && (
                <MoneyRow
                  label="Plus processing fees recovered from customers"
                  value={fmtUGX(totals.paymentProcessingFee)}
                  sub={`${fmtPct(totals.railPassedThroughPct, 0)} of processing charges recovered`}
                />
              )}
              <div
                style={{
                  ...S.row,
                  borderBottom: "none",
                  paddingTop: 10,
                  fontWeight: 800,
                  color: C.ink,
                }}
              >
                <span>Net revenue</span>
                <span>{fmtUGX(totals.netTake)}</span>
              </div>
            </div>

            <div style={S.panel}>
              <SectionTitle first>Third-party settlements</SectionTitle>
              <MoneyRow label="Vendor payouts" value={fmtUGX(totals.vendorPayout)} />
              <MoneyRow label="Rider payouts" value={fmtUGX(totals.riderEarnings)} />
              <MoneyRow label="3PL partner payouts" value={fmtUGX(totals.partnerEarnings)} />
              <MoneyRow label="Delivery fees collected" value={fmtUGX(totals.deliveryFee)} />
              {totals.deliveryOfferCost > 0 && (
                <MoneyRow label="Delivery fees funded by Lusuku (customer offers)" value={fmtUGX(totals.deliveryOfferCost)} />
              )}
              <MoneyRow label="Goods value" value={fmtUGX(totals.goods)} />
            </div>
          </div>

          <SectionTitle>Net revenue by day</SectionTitle>
          <div style={S.chartBox}>
            <Bar data={trendChart.data} options={trendChart.options} />
          </div>

          <div style={S.grid2}>
            <div style={S.panel}>
              <SectionTitle first>Collections by payment method</SectionTitle>
              <ShareList rows={railRows} emptyText="No completed orders in this window." />
              <div style={{ marginTop: 12 }}>
                <MoneyRow label="Processing charges in total" value={fmtUGX(totals.railFee)} />
                <MoneyRow label="Recovered from customers" value={fmtUGX(totals.railBorneByCustomer)} />
                <MoneyRow label="Borne by Lusuku" value={fmtUGX(totals.railBorneByLusuku)} />
              </div>
              <div style={{ ...S.muted, marginTop: 10 }}>
                {data?.railFeePolicy?.chargeCustomer
                  ? `Customers are currently charged ${(
                      data.railFeePolicy.label || "a processing fee"
                    ).toLowerCase()}.`
                  : "Lusuku currently absorbs payment processing charges. The policy is set under Payment fees."}
              </div>
            </div>
            <div style={S.panel}>
              <SectionTitle first>Vehicles dispatched</SectionTitle>
              <ShareList rows={vehicleRows} emptyText="No completed deliveries in this window." />
            </div>
            <div style={S.panel}>
              <SectionTitle first>Delivery zones</SectionTitle>
              <ShareList rows={zoneRows} emptyText="No completed deliveries in this window." />
            </div>
            {(data?.byCountry || []).length > 1 && (
              <div style={S.panel}>
                <SectionTitle first>Markets</SectionTitle>
                <ShareList
                  rows={data.byCountry.map((c) => ({
                    key: c.country,
                    label: c.country,
                    amount: c.customerPaid,
                    sub: `net revenue ${fmtUGX(c.netTake)}`,
                    color: C.navy,
                  }))}
                />
              </div>
            )}
          </div>

          <SectionTitle>Vendors by commission earned (completed orders)</SectionTitle>
          <div style={S.scroll}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>#</th>
                  <th style={S.th}>Vendor</th>
                  <th style={S.th}>Type</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Goods value</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Commission</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Effective rate</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Vendor payout</th>
                  <th style={{ ...S.th, textAlign: "right" }}>Orders</th>
                </tr>
              </thead>
              <tbody>
                {(data?.topVendors || []).length === 0 ? (
                  <tr>
                    <td style={S.td} colSpan={8}>
                      <span style={S.muted}>No completed vendor sales in this window.</span>
                    </td>
                  </tr>
                ) : (
                  data.topVendors.map((v, i) => (
                    <tr key={v.vendorId}>
                      <td style={S.td}>{i + 1}</td>
                      <td
                        style={{
                          ...S.td,
                          fontWeight: 600,
                          color: C.body,
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {v.name}
                      </td>
                      <td style={S.td}>
                        <span style={S.pill}>{v.vendorType || "other"}</span>
                      </td>
                      <td style={S.tdNum}>{fmtUGX(v.goods)}</td>
                      <td style={{ ...S.tdNum, fontWeight: 700, color: C.ink }}>
                        {fmtUGX(v.commission)}
                      </td>
                      <td style={S.tdNum}>{fmtPct(v.effectiveCommissionPct)}</td>
                      <td style={S.tdNum}>{fmtUGX(v.payout)}</td>
                      <td style={S.tdNum}>{v.orders}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={S.note}>
            <strong>Basis.</strong> Figures are recognised on completed orders only (delivered and
            not cancelled); open and cancelled orders appear in the pipeline, not in revenue.
            Gross revenue is the commission on goods plus the delivery margin. Output VAT on the
            commission and payment processing charges come out of it; processing is charged on the
            whole collected amount, goods included, not on Lusuku's share. That is why an average
            order of {fmtUGX(totals.averageOrderValue)} yields {fmtUGX(totals.averageNetTake)} in
            net revenue.{" "}
            {totals.railBorneByCustomer > 0
              ? `Customers covered ${fmtUGX(totals.railBorneByCustomer)} of processing charges in this window, ${fmtPct(totals.railPassedThroughPct, 0)} of the total.`
              : "Enabling the pass-through under Payment fees moves that cost to the customer."}{" "}
            {railConfidenceNote(totals)}
          </div>
        </>
      )}
    </div>
  );
}
